import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import JSZip from 'jszip';
import { classifyDocument } from '../../../lib/documents/classifier';
import type { Readable } from 'stream';

// Disable default body parser for multipart/form-data
export const config = {
  api: {
    bodyParser: false,
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SUPPORTED_EXTENSIONS = new Set(['.pdf', '.png', '.jpg', '.jpeg', '.xlsx', '.csv']);
const STORAGE_BUCKET = 'documentos';
const MAX_FILES = 200;

const MIME_MAP: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv': 'text/csv',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function readRawBody(req: NextApiRequest): Promise<Buffer> {
  const chunks: Buffer[] = [];
  for await (const chunk of req as unknown as Readable) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks);
}

interface ParsedMultipart {
  fields: Record<string, string>;
  file?: { filename: string; content: Buffer; contentType: string };
}

async function parseMultipart(req: NextApiRequest): Promise<ParsedMultipart> {
  const contentType = req.headers['content-type'] || '';
  const boundaryMatch = contentType.match(/boundary=(.+)/);
  if (!boundaryMatch) {
    throw new Error('No se encontró boundary en el Content-Type multipart.');
  }

  const boundary = boundaryMatch[1];
  const rawBody = await readRawBody(req);
  const bodyStr = rawBody.toString('latin1');
  const parts = bodyStr.split(`--${boundary}`).filter(
    (p) => p.trim() !== '' && p.trim() !== '--'
  );

  const result: ParsedMultipart = { fields: {} };

  for (const part of parts) {
    const headerEnd = part.indexOf('\r\n\r\n');
    if (headerEnd === -1) continue;

    const headerSection = part.substring(0, headerEnd);
    const bodySection = part.substring(headerEnd + 4).replace(/\r\n$/, '');

    const nameMatch = headerSection.match(/name="([^"]+)"/);
    if (!nameMatch) continue;
    const fieldName = nameMatch[1];

    const filenameMatch = headerSection.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      const contentTypeMatch = headerSection.match(/Content-Type:\s*(.+)/i);
      const start = rawBody.indexOf(
        Buffer.from('\r\n\r\n', 'latin1'),
        rawBody.indexOf(Buffer.from(headerSection.substring(0, 40), 'latin1'))
      ) + 4;
      const endMarker = Buffer.from(`\r\n--${boundary}`, 'latin1');
      const end = rawBody.indexOf(endMarker, start);
      result.file = {
        filename: filenameMatch[1],
        content: rawBody.subarray(start, end !== -1 ? end : undefined),
        contentType: contentTypeMatch ? contentTypeMatch[1].trim() : 'application/octet-stream',
      };
    } else {
      result.fields[fieldName] = bodySection;
    }
  }

  return result;
}

function getExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex >= 0 ? filename.substring(dotIndex).toLowerCase() : '';
}

function buildStoragePath(empresaId: string, filename: string): string {
  const timestamp = Date.now();
  const safe = filename.replace(/[^\w.\-]/g, '_');
  return `${empresaId}/${timestamp}_${safe}`;
}

// ─── Handler ─────────────────────────────────────────────────────────────────

/**
 * POST /api/documents/upload-zip
 *
 * Accepts multipart/form-data with a ZIP file.
 * Extracts files, filters for supported types, uploads to Supabase storage,
 * and creates document entries in the 'documentos' table.
 *
 * Form fields:
 *   - file: ZIP file
 *   - empresa_id: string (required)
 *   - cliente_id: string (optional)
 *
 * Returns: { success, uploaded, skipped, errors }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // Parse multipart form data
    const formData = await parseMultipart(req);

    const empresaId = formData.fields['empresa_id'];
    const clienteId = formData.fields['cliente_id'] || null;

    if (!empresaId) {
      return res.status(400).json({ success: false, error: 'empresa_id es requerido.' });
    }

    if (!formData.file) {
      return res.status(400).json({ success: false, error: 'No se proporcionó archivo ZIP.' });
    }

    // Load ZIP
    let zip: JSZip;
    try {
      zip = await JSZip.loadAsync(formData.file.content);
    } catch {
      return res.status(400).json({ success: false, error: 'El archivo no es un ZIP válido.' });
    }

    // Collect valid entries (ignore directories, macOS artifacts, hidden files)
    const entries = Object.entries(zip.files).filter(
      ([name, entry]) =>
        !entry.dir &&
        !name.startsWith('__MACOSX') &&
        !name.startsWith('.') &&
        !name.includes('/.')
    );

    if (entries.length === 0) {
      return res.status(400).json({ success: false, error: 'El ZIP no contiene archivos.' });
    }

    if (entries.length > MAX_FILES) {
      return res.status(400).json({
        success: false,
        error: `El ZIP contiene más de ${MAX_FILES} archivos.`,
      });
    }

    let uploaded = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const [fullPath, entry] of entries) {
      const filename = fullPath.split('/').pop() || fullPath;
      const ext = getExtension(filename);

      // Filter only supported extensions
      if (!SUPPORTED_EXTENSIONS.has(ext)) {
        skipped++;
        continue;
      }

      try {
        const fileBuffer = await entry.async('nodebuffer');

        if (!fileBuffer || fileBuffer.length === 0) {
          skipped++;
          continue;
        }

        const mimeType = MIME_MAP[ext] || 'application/octet-stream';

        // Upload to Supabase Storage
        const storagePath = buildStoragePath(empresaId, filename);
        const { error: uploadError } = await supabaseAdmin.storage
          .from(STORAGE_BUCKET)
          .upload(storagePath, fileBuffer, {
            contentType: mimeType,
            upsert: false,
          });

        if (uploadError) {
          errors.push(`Error subiendo ${filename}: ${uploadError.message}`);
          skipped++;
          continue;
        }

        // Get public URL
        const { data: urlData } = supabaseAdmin.storage
          .from(STORAGE_BUCKET)
          .getPublicUrl(storagePath);
        const publicUrl = urlData?.publicUrl ?? '';

        // Classify by filename
        const classification = classifyDocument(filename, mimeType);

        // Create document record in 'documentos' table
        const { error: insertError } = await supabaseAdmin
          .from('documentos')
          .insert({
            nombre: filename,
            url_archivo: publicUrl,
            tipo: classification.tipo,
            empresa_id: empresaId,
            cliente_id: clienteId,
            estado: 'pendiente',
          });

        if (insertError) {
          errors.push(`Error registrando ${filename}: ${insertError.message}`);
          skipped++;
          continue;
        }

        uploaded++;
      } catch (fileErr: unknown) {
        const msg = fileErr instanceof Error ? fileErr.message : 'Error desconocido';
        errors.push(`Error procesando ${filename}: ${msg}`);
        skipped++;
      }
    }

    return res.status(200).json({
      success: true,
      uploaded,
      skipped,
      errors,
    });
  } catch (error: any) {
    console.error('[upload-zip] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al procesar el archivo ZIP.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
